// ViewDocVersionStrip — spec decision 6: "One row per document, version history
// inside it — current version by default; superseded / voided / cancelled
// versions reachable from a version strip."
//
// Newest first, left to right. The strip is hidden when there is only one
// version: a lone chip that goes nowhere is chrome, and the header already
// states the document's state and when it went out.

import { formatWhen } from '@/lib/commandCenterClient';
import { StatusChip } from '@/components/oc/bits';
import { docStateLabel, docStateTone, orderVersions } from '@/lib/viewDocs';
import type { DocVersion } from '@/lib/viewDocsClient';

/** "Version 3" when the server didn't name it; positional, oldest = 1. */
function versionName(version: DocVersion, ordinal: number): string {
  return version.label?.trim() || `Version ${ordinal}`;
}

export function ViewDocVersionStrip({
  versions,
  selectedId,
  onSelect,
}: {
  versions: DocVersion[];
  selectedId: string | null;
  onSelect: (versionId: string) => void;
}): React.JSX.Element | null {
  const ordered = orderVersions(versions);
  if (ordered.length < 2) return null;

  return (
    <div>
      <div className="oc-kicker" style={{ marginBottom: 'var(--space-2)' }}>
        Versions — newest first
      </div>
      <div className="vd-strip" role="group" aria-label="Document versions">
        {ordered.map((version, i) => {
          const ordinal = ordered.length - i;
          const when = version.sentAt ?? version.createdAt ?? null;
          const selected = version.id === selectedId;
          return (
            <button
              key={version.id}
              type="button"
              className="vd-strip-item"
              aria-pressed={selected}
              onClick={() => onSelect(version.id)}
            >
              <span className="vd-strip-name">
                {versionName(version, ordinal)}
                {version.current ? ' · current' : ''}
              </span>
              <span className="vd-strip-meta">
                <StatusChip tone={docStateTone(version.state)}>
                  {docStateLabel(version)}
                </StatusChip>
                {when ? ` · ${formatWhen(when)}` : ''}
                {version.viewable === false && (
                  // Selectable, deliberately: its panel still describes what
                  // that version said, even when the bytes are gone.
                  <span className="vd-strip-note"> · details only</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
